import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { cn } from '../../../utils/cn';
import { useNavigate } from 'react-router-dom';
import { base_url } from 'Backend/api';
import toast, { Toaster } from 'react-hot-toast';
import Lottie from 'lottie-react';
import warningAnimation from '../../../../public/assets/lottie/cancel.json';

function findRestaurantCartLine(stores, restaurantSlug, dishSlug) {
  if (!Array.isArray(stores) || !restaurantSlug || !dishSlug) return null;
  for (const store of stores) {
    if (store.store_type !== "restaurant") continue;
    if (store.store_slug !== restaurantSlug) continue;
    const items = store.items || [];
    const matches = [];
    for (const item of items) {
      const lineSlug = item.dish?.slug ?? item.dish_slug ?? item.slug;
      if (lineSlug && lineSlug === dishSlug) matches.push(item);
    }
    if (matches.length === 0) return null;
    return matches[0];
  }
  return null;
}

const ProductCustomizationModal = ({
  isOpen,
  onClose,
  product,
  restaurantName,
  restaurantSlug,
  minOrderPrice,
}) => {
  const navigate = useNavigate();
  const addonsSectionRef = useRef(null);
  const [quantity, setQuantity] = useState(1);
  const [cartItemId, setCartItemId] = useState(null);
  const [qtyBusy, setQtyBusy] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState(['جبنة موزاريلا']);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedDrinks, setSelectedDrinks] = useState([]);
  const [isAddonsExpanded, setIsAddonsExpanded] = useState(true);
  const [isDrinksExpanded, setIsDrinksExpanded] = useState(true);
  const [minOrderWarningMessage, setMinOrderWarningMessage] = useState(null);

  useEffect(() => {
    setSelectedIngredients([]);
    setSelectedAddons([]);
    setSelectedDrinks([]);
    setMinOrderWarningMessage(null);
  }, [product]);

  const syncCartLine = useCallback(async () => {
    const dishSlug = product?.slug;
    if (!isOpen || !dishSlug || !restaurantSlug) {
      setCartItemId(null);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      setCartItemId(null);
      return;
    }
    try {
      const res = await fetch(`${base_url}/api/cart`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!data.status) {
        setCartItemId(null);
        return;
      }
      const line = findRestaurantCartLine(data.data?.stores || [], restaurantSlug, dishSlug);
      if (line?.id != null) {
        setCartItemId(line.id);
        setQuantity(Math.max(1, Number(line.quantity) || 1));
      } else {
        setCartItemId(null);
        setQuantity(1);
      }
    } catch {
      setCartItemId(null);
    }
  }, [isOpen, product?.slug, restaurantSlug]);

  useEffect(() => {
    void syncCartLine();
  }, [syncCartLine]);

  const patchQuantity = useCallback(async (newQty) => {
    if (cartItemId == null || newQty < 1) return;
    const token = localStorage.getItem("token");
    setQtyBusy(true);
    try {
      const response = await fetch(`${base_url}/api/cart/items/${cartItemId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ quantity: newQty }),
      });
      const data = await response.json().catch(() => ({}));
      if (data.status) {
        setQuantity(newQty);
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        toast.error(data.message || "تعذر تحديث الكمية");
      }
    } catch {
      toast.error("تعذر تحديث الكمية");
    } finally {
      setQtyBusy(false);
    }
  }, [cartItemId]);

  const ingredients = product?.ingredients || [];
  const addons = product?.addons || [];
  const drinks = product?.drinks || [];
  const basePrice = product?.base_price || 0;

  const handleIngredientToggle = (ingredientName) => {
    setSelectedIngredients((prev) =>
      prev?.includes(ingredientName) ?
        prev?.filter((i) => i !== ingredientName) :
        [...prev, ingredientName]
    );
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons((prev) =>
      prev?.includes(addonId) ?
        prev?.filter((id) => id !== addonId) :
        [...prev, addonId]
    );
  };

  const handleDrinkToggle = (drinkId) => {
    const willAdd = !selectedDrinks.includes(drinkId);
    setSelectedDrinks((prev) =>
      prev?.includes(drinkId) ?
        prev?.filter((id) => id !== drinkId) :
        [...prev, drinkId]
    );

    if (willAdd && addons?.length > 0) {
      setIsAddonsExpanded(true);
      window.setTimeout(() => {
        addonsSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 140);
    }
  };

  const calculateTotal = () => {
    const addonsTotal = selectedAddons?.reduce((sum, addonId) => {
      const addon = addons?.find((a) => a?.id === addonId);
      return sum + (addon?.price || 0);
    }, 0);

    const drinksTotal = selectedDrinks?.reduce((sum, drinkId) => {
      const drink = drinks?.find((d) => d?.id === drinkId);
      return sum + (drink?.price || 0);
    }, 0);

    return basePrice * quantity + addonsTotal + drinksTotal;
  };

  const handleQuickOrderNavigate = () => {
    const total = calculateTotal();
    const min = Number(minOrderPrice);
    if (Number.isFinite(min) && min > 0 && total < min) {
      const remaining = Math.ceil(Math.max(0, min - total));
      setMinOrderWarningMessage(
        `لم يكتمل الحد الأدنى للطلب (${min.toLocaleString()} ل.س). باقي ${remaining.toLocaleString()}ل.س. زِد الكمية أو أضف اضافات أو مشروبات.`,
      );
      return;
    }
    navigate("/completeQuickOrder", {
      state: {
        product,
        restaurantSlug,
        quantity,
        selectedAddons,
        selectedDrinks,
        selectedIngredients,
        ingredients,
        total,
      },
    });
  };

  if (!isOpen) return null;


  const handleAddToCart = async () => {
    try {
      const token = localStorage.getItem("token");

      const body = {
        store_type: "restaurant",
        restaurant_slug: restaurantSlug,
        dish_slug: product?.slug,
        quantity: quantity,
        addons: selectedAddons.map(id => ({
          dish_addon_id: id,
          quantity: 1
        })),
        drinks: selectedDrinks.map(id => ({
          drink_id: id,
          quantity: 1
        })),
        removed_ingredients: ingredients
          .filter(i => !selectedIngredients.includes(i.name))
          .map(i => i.id)
      };

      const response = await fetch(`${base_url}/api/cart/items`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.status) {
        toast.success("تمت الإضافة إلى السلة بنجاح ");

        window.dispatchEvent(new Event("cartUpdated"));
        await syncCartLine();

        onClose();
      } else {
        toast.error("حدث خطأ أثناء الإضافة");
      }

    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ في الاتصال بالخادم");
    }
  };


  

  

  return (
    <>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-60 transition-opacity duration-300"
        onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-70 flex items-end md:items-center md:justify-center font-[cairo]">
        <div
          className="bg-background w-full md:max-w-lg rounded-t-[16px] rounded-b-none max-h-[85vh] overflow-y-auto scrollbar-hide animate-slide-up md:animate-fade-in"
          onClick={(e) => e?.stopPropagation()}>

          {/* Header: إغلاق يمين، الاسم في المنتصف (توازن مع عمود فارغ يسار) */}
          <div
            dir="ltr"
            className="sticky top-0 z-10 flex items-center bg-primary px-4 py-3 text-primary-foreground"
          >
            <div className="h-8 w-8 shrink-0" aria-hidden />
            <h2
              dir="rtl"
              className="min-w-0 flex-1 truncate text-center text-base font-medium md:text-lg font-[cairo]"
            >
              {restaurantName || 'مطعم الأصيل'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/10"
              aria-label="إغلاق"
            >
              <Icon name="X" size={20} color="currentColor" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-4 space-y-4">
            {/* Product Image */}
            <div className="flex justify-center">
              <div className="w-36 h-36 sm:w-40 sm:h-40 md:w-44 md:h-44 rounded-full overflow-hidden ring-1 ring-black/5 transition-transform duration-300">
                <Image
                  src={`${base_url}/storage/app/public/${product?.image}`}
                  alt={product?.imageAlt}
                  className="w-full h-full object-cover" />

              </div>
            </div>

            {/* Product Title & Description */}
            <div className="text-center space-y-1.5">
              <h3 className="text-lg font-bold text-foreground font-[cairo]">
                {product?.name || 'بيتزا الفصول الأربعة'}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                {product?.description || 'بيتزا الفصول الأربعة والجبن والمكونات الخاصة والجبن والمكونات الخاصة'}
              </p>
            </div>

            {/* الكمية — نفس ترتيب الصورة: + يسار، العدد، − يمين (dir=ltr) */}
            <div className="flex justify-center">
              <div
                dir="ltr"
                className="flex items-center justify-center gap-6"
              >
                <button
                  type="button"
                  aria-label="زيادة الكمية"
                  disabled={qtyBusy}
                  onClick={() => {
                    if (qtyBusy) return;
                    if (cartItemId != null) void patchQuantity(quantity + 1);
                    else setQuantity((q) => q + 1);
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f0f0f2] text-gray-900 transition-colors hover:bg-[#e6e6e9] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon name="Plus" size={18} color="currentColor" />
                </button>
                <span className="min-w-[2.5rem] text-center text-lg font-semibold tabular-nums text-gray-900">
                  {quantity}
                </span>
                <button
                  type="button"
                  aria-label="تقليل الكمية"
                  disabled={qtyBusy || quantity <= 1}
                  onClick={() => {
                    if (qtyBusy || quantity <= 1) return;
                    if (cartItemId != null) void patchQuantity(quantity - 1);
                    else setQuantity((q) => Math.max(1, q - 1));
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f0f0f2] text-gray-900 transition-colors hover:bg-[#e6e6e9] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icon name="Minus" size={18} color="currentColor" />
                </button>
              </div>
            </div>

            {/* Ingredients Section */}
            {ingredients?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-base font-bold text-foreground font-[cairo]">المكونات</h4>
              <p className="text-xs text-muted-foreground">
                يرجى اختيار المكونات غير المطلوبة
              </p>
              {/* <div className="flex flex-wrap gap-3">
                {ingredients?.map((ingredient) =>
                  <button
                    key={ingredient?.id}
                    onClick={() => handleIngredientToggle(ingredient?.name)}
                    className={cn(
                      "px-6 py-2 rounded-full text-sm font-medium transition-all duration-250",
                      selectedIngredients?.includes(ingredient?.name) ?
                        "bg-primary text-primary-foreground" :
                        "bg-white border-2 border-border text-foreground hover:border-primary"
                    )}>

                    {ingredient?.name}
                  </button>
                )}
              </div> */}
              <div className="flex flex-wrap gap-3">
                {ingredients?.map((ingredient) => {
                  const isSelected = selectedIngredients?.includes(ingredient?.name);

                  return (
                    <button
                      key={ingredient?.id}
                      onClick={() => handleIngredientToggle(ingredient?.name)}
                      className={cn(
                        "px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 border",
                        isSelected
                          ? "bg-white text-red-600 border-red-500 line-through"
                          : "bg-white border-gray-300 text-foreground hover:border-red-400"
                      )}
                    >
                      {ingredient?.name}
                    </button>
                  );
                })}
              </div>
            </div>
           )}

            {/* Add-ons Section */}
            {addons?.length > 0 && (
            <div ref={addonsSectionRef} className="scroll-mt-24 space-y-3">
              <button
                type="button"
                onClick={() => setIsAddonsExpanded(!isAddonsExpanded)}
                className="flex items-center justify-between w-full text-right">

                <h4 className="text-base font-bold text-foreground font-[cairo]">إضافات أخرى</h4>
                <Icon
                  name={isAddonsExpanded ? "ChevronUp" : "ChevronDown"}
                  size={20}
                  color="currentColor" />

              </button>

              {isAddonsExpanded &&
                <div className="space-y-2">
                  {addons?.map((addon) =>
                    <div
                      key={addon?.id}
                      className="flex items-center justify-between py-1.5">

                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <Checkbox
                          className="items-center !space-x-0"
                          checked={selectedAddons?.includes(addon?.id)}
                          onChange={() => handleAddonToggle(addon?.id)} />

                        <span className="text-sm text-foreground truncate">{addon?.name}</span>
                      </div>
                      <span className="text-primary shrink-0 text-sm font-bold">+{addon?.price} ل.س</span>
                    </div>
                  )}
                </div>
              }
            </div>
             )}

            {/* Drinks Section */}
            {drinks?.length > 0 && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setIsDrinksExpanded(!isDrinksExpanded)}
                className="flex items-center justify-between w-full text-right">

                <h4 className="text-base font-bold text-foreground font-[cairo]">المشروبات</h4>
                <Icon
                  name={isDrinksExpanded ? "ChevronUp" : "ChevronDown"}
                  size={20}
                  color="currentColor" />

              </button>

              {isDrinksExpanded &&
                <div className="space-y-2">
                  {drinks?.map((drink) =>
                    <div
                      key={drink?.id}
                      className="flex items-center justify-between py-1.5"
                    >

                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <Checkbox
                          className="items-center !space-x-0"
                          checked={selectedDrinks?.includes(drink?.id)}
                          onChange={() => handleDrinkToggle(drink?.id)} />

                        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md md:h-10 md:w-10">
                          <Image

                            src={`${base_url}/storage/app/public/${drink?.image}`}
                            alt={drink?.imageAlt}
                            className="h-full w-full object-cover" />

                        </div>
                        <span className="text-sm text-foreground">{drink?.name}</span>
                      </div>
                      <span className="text-primary shrink-0 text-sm font-bold">+{drink?.price} د.س</span>
                    </div>
                  )}
                </div>
              }
            </div>
            )}


          </div>

          {/* Sticky Bottom Button */}
          {/* <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4">
            <button
              onClick={handleAddToCart}
              className="w-full bg-primary text-primary-foreground rounded-[16px] py-3 flex items-center justify-between px-6 hover:bg-primary/90 transition-colors warm-shadow-lg">


              <span className="text-lg font-medium">أضف الى السلة</span>
              <span className="text-lg font-medium">{calculateTotal()} ل.س</span>
            </button>
          </div> */}
          {/* Sticky Bottom Section */}
<div className="sticky bottom-0 bg-white border-t border-border px-3 py-2.5">
  
  <div className="flex items-center justify-between gap-2">

    {/* 💰 السعر */}
    <div className="text-sm font-bold whitespace-nowrap md:text-[15px]">
      {calculateTotal()} ل.س
    </div>
    <div>
<button
  type="button"
  onClick={handleQuickOrderNavigate}
  className="bg-white text-[#F93209] px-3 py-2 rounded-[4px] ml-2 text-sm border border-[#F93209]"
>
  طلب سريع
</button>

    {/* 🛒 أضف للسلة */}
    <button
      onClick={handleAddToCart}
      className="bg-[#F93209] text-primary-foreground px-4 py-2 rounded-[4px] text-sm hover:bg-primary/90 transition-colors"
    >
     أضف إلى السلة
    </button>
    </div>
    {/* ⚡ طلب سريع */}
    

  </div>

</div>

        </div>
      </div>

      {minOrderWarningMessage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="min-order-warning-title"
          onClick={() => setMinOrderWarningMessage(null)}
        >
          <div
            className="mx-4 w-full max-w-[300px] rounded-[8px] bg-white p-6 text-center shadow-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <Lottie
              animationData={warningAnimation}
              loop={false}
              className="mx-auto h-20 w-20 md:h-32 md:w-32"
            />
            <h2
              id="min-order-warning-title"
              className="mt-1 font-[cairo] text-base font-bold text-gray-900"
            >
              تنبيه
            </h2>
            <p className="mt-2 font-[cairo] text-sm font-medium leading-relaxed text-gray-700">
              {minOrderWarningMessage}
            </p>
            <button
              type="button"
              onClick={() => setMinOrderWarningMessage(null)}
              className="mt-5 w-full rounded-[4px] bg-[#F93209] py-2.5 font-[cairo] text-sm font-semibold text-white transition-colors hover:bg-[#e02d08]"
            >
              حسناً
            </button>
          </div>
        </div>
      )}
    </>);

};

export default ProductCustomizationModal;